// src/layout/AppLayout.js

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, Outlet, useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { signOut, onAuthStateChanged } from "firebase/auth";
import SidebarNav from "../components/navigation/SidebarNav";
import MobileMenu from "../components/navigation/MobileMenu";
import MobileBottomNav from "../components/navigation/MobileBottomNav";
import Logo from "../assets/Logo.png";
import NotificationBell from "../components/notifications/NotificationBell";
import { Menu, UserCircle2 } from "lucide-react";
import { getFeatureAccessMap, getMaintenanceBannerConfig, mergeFeatureFlags } from "../utils/featureAccess";

/**
 * Dev-only emails for internal tools / footer, etc.
 */
const DEV_EMAILS = ["roserobin13@gmail.com"];

/**
 * Support contact (configure if you want)
 */
const SUPPORT_EMAIL =
  process.env.REACT_APP_SUPPORT_EMAIL || "support@thesupplyhawk.com";
const SALES_EMAIL = "sleclair@thesupplyhawk.com";
const SALES_PHONE = "5419150110";
const TECH_SUPPORT_PHONE = "8502619099";

// Map routes -> header title + subtitle (NOTE: keys are WITHOUT /app prefix)
const routeMeta = {
  "/dashboard": {
    title: "Dashboard",
    subtitle: "Overview of your company, team, and sample activity.",
  },
  "/sample-orders": {
    title: "Sample Orders",
    subtitle: "Create and track sample requests.",
  },
  "/orders": {
    title: "Orders",
    subtitle: "Create and manage approval-first customer orders.",
  },
  "/accounts": {
    title: "Accounts",
    subtitle: "Your customers, prospects, and restaurant locations.",
  },
  "/inventory": {
    title: "Inventory",
    subtitle: "Products available to sample and sell.",
  },
  "/tasks": {
    title: "Tasks",
    subtitle: "Organize, assign, and track work across your team.",
  },
  "/grids": {
    title: "Grids",
    subtitle: "Edit inventory and accounts in a spreadsheet-style workspace.",
  },
  "/team": {
    title: "Team",
    subtitle: "Manage your staff, roles, and access.",
  },
  "/teams": {
    title: "Team",
    subtitle: "Manage your staff, roles, and access.",
  },
  "/groups": {
    title: "Team",
    subtitle: "Manage your staff, roles, and access.",
  },
  "/territories-map": {
    title: "Map",
    subtitle: "View and manage territories and assignments.",
  },
  "/territories": {
    title: "Map",
    subtitle: "View and manage territories and assignments.",
  },
  "/map": {
    title: "Map",
    subtitle: "View and manage territories and assignments.",
  },
  "/settings": {
    title: "Profile & Company",
    subtitle: "Manage your profile, company info, and team access.",
  },
  "/admin": {
    title: "Admin",
    subtitle: "Feature flags, billing, and subscription controls.",
  },
  "/notifications": {
    title: "Notifications",
    subtitle: "Alerts and updates related to your account.",
  },
  "/dev-tools": {
    title: "Developer Tools",
    subtitle: "Repair user/company records and run test utilities.",
  },
};

const AppLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [companyName, setCompanyName] = useState("Your Company");
  const [companyData, setCompanyData] = useState(null);
  const [developerFeatureFlags, setDeveloperFeatureFlags] = useState({});

  // 🔔 For notifications
  const [currentUserId, setCurrentUserId] = useState(null);
  const [companyId, setCompanyId] = useState(null);

  // First name for profile label
  const [userFirstName, setUserFirstName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [userPhotoUrl, setUserPhotoUrl] = useState("");
  const [userPlanType, setUserPlanType] = useState(null);
  const [userSubscriptionActive, setUserSubscriptionActive] = useState(false);

  // Track role (used to hide sidebar for customers)
  const [userRole, setUserRole] = useState("");
  const [themeMode, setThemeMode] = useState(() => localStorage.getItem("supplyhawk-theme") || "light");
  const [networkOnline, setNetworkOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine
  );

  // 🔧 Dev-only footer/test tools
  const [isDev, setIsDev] = useState(false);

  // Profile dropdown
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);

  // Contact + Legal modals
  const [contactOpen, setContactOpen] = useState(false);
  const [contactForms, setContactForms] = useState({
    support: { subject: "", message: "" },
    sales: { subject: "", message: "" },
  });
  const [contactSendingTarget, setContactSendingTarget] = useState("");
  const [contactFeedback, setContactFeedback] = useState("");
  const [contactRemainingToday, setContactRemainingToday] = useState(null);
  const [legalOpen, setLegalOpen] = useState(false);
  const [legalTab, setLegalTab] = useState("terms"); // "terms" | "privacy"

  const functions = getFunctions(undefined, "us-central1");
  const sendContactMessage = useMemo(
    () => httpsCallable(functions, "sendContactMessage"),
    [functions]
  );

  // -----------------------------
  // ✅ BASE PATH FIX (/app support)
  // -----------------------------
  const rawPath = location.pathname.split("?")[0].split("#")[0];

  // If you host the staff app under /app, this will be "/app"; otherwise ""
  const APP_BASE = rawPath.startsWith("/app") ? "/app" : "";

  const stripBase = (p) => {
    if (!APP_BASE) return p;
    if (p === APP_BASE) return "/"; // edge case
    return p.startsWith(APP_BASE + "/") ? p.slice(APP_BASE.length) : p;
  };

  const buildAppPath = (p) => {
    const cleaned = p.startsWith("/") ? p : `/${p}`;
    return APP_BASE ? `${APP_BASE}${cleaned}` : cleaned;
  };

  // Normalize path for route meta (strip /app)
  const pathKey = stripBase(rawPath);

  // Treat these as "customer portal" routes (AFTER base strip)
  const isCustomerRoute =
    pathKey === "/customer" ||
    pathKey.startsWith("/customer/") ||
    pathKey.startsWith("/customer-") ||
    pathKey.startsWith("/portal") ||
    pathKey.startsWith("/customer-portal");

  const isCustomerRole = (userRole || "").toLowerCase() === "customer";

  // ✅ Hide sidebar for customer portal routes or customer role
  const hideSidebar = isCustomerRoute || isCustomerRole;
  const showMobileShell = !hideSidebar;

  // Send test email (dev/testing)
  const handleTestResend = async () => {
    try {
      const result = await httpsCallable(functions, "testResend")();
      console.log("testResend result:", result.data);
      alert("Test email sent!");
    } catch (err) {
      console.error("testResend error:", err);
      alert("Error sending test email.");
    }
  };

  const updateContactField = (target, field, value) => {
    setContactForms((prev) => ({
      ...prev,
      [target]: {
        ...prev[target],
        [field]: value,
      },
    }));
  };

  const handleSendContact = async (target) => {
    const payload = contactForms[target] || {};
    const subject = (payload.subject || "").trim();
    const message = (payload.message || "").trim();
    if (!subject || !message) {
      setContactFeedback("Add both a subject and message before sending.");
      return;
    }

    setContactSendingTarget(target);
    setContactFeedback("");
    try {
      const result = await sendContactMessage({ target, subject, message });
      const remaining = result?.data?.remainingToday;
      setContactRemainingToday(typeof remaining === "number" ? remaining : null);
      setContactFeedback(result?.data?.message || "Email sent.");
      setContactForms((prev) => ({
        ...prev,
        [target]: { subject: "", message: "" },
      }));
    } catch (err) {
      console.error("[AppLayout] sendContactMessage failed:", err);
      setContactFeedback(err?.message || "Could not send email.");
    } finally {
      setContactSendingTarget("");
    }
  };

  const handleLogout = async () => {
    try {
      setProfileMenuOpen(false);
      await signOut(auth);
      navigate("/login");
    } catch (err) {
      console.error("[AppLayout] Sign out error:", err);
      alert("Could not sign out.");
    }
  };

  useEffect(() => {
    // Close profile menu on outside click
    const onClickOutside = (e) => {
      if (!profileMenuRef.current) return;
      if (!profileMenuRef.current.contains(e.target)) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    // Keep company/user header data in sync with auth
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setCompanyName("Your Company");
        setCompanyData(null);
        setCurrentUserId(null);
        setCompanyId(null);
        setUserFirstName("");
        setUserEmail("");
        setUserPhotoUrl("");
        setUserPlanType(null);
        setUserSubscriptionActive(false);
        setUserRole("");
        setIsDev(false);
        return;
      }

      const email = (user.email || "").toLowerCase();
      const dev = DEV_EMAILS.includes(email);
      setIsDev(dev);
      setUserEmail(email);
      setUserPhotoUrl(user.photoURL || "");

      try {
        setCurrentUserId(user.uid);

        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();

          // ✅ role for layout decisions
          setUserRole(userData?.role || "");
          setUserPlanType(userData?.planType || null);
          setUserSubscriptionActive(userData?.subscriptionActive === true);
          setUserPhotoUrl(userData?.photoURL || user.photoURL || "");
          const nextTheme = (userData?.preferredTheme || localStorage.getItem("supplyhawk-theme") || "light").toString().toLowerCase() === "dark" ? "dark" : "light";
          setThemeMode(nextTheme);

          // first name for profile label
          let firstName = "";
          if (userData.fullName) firstName = userData.fullName.split(" ")[0];
          else if (userData.firstName) firstName = userData.firstName;
          else if (user.displayName) firstName = user.displayName.split(" ")[0];
          else if (user.email) firstName = user.email.split("@")[0];
          setUserFirstName(firstName);

          // businessName on user profile
          if (userData.businessName) setCompanyName(userData.businessName);

          // company doc via companyId
          if (userData.companyId) {
            const compId = userData.companyId;
            setCompanyId(compId);

            const [companyDoc, globalControlsDoc] = await Promise.all([
              getDoc(doc(db, "companies", compId)),
              getDoc(doc(db, "platformSettings", "developerControls")),
            ]);
            if (companyDoc.exists()) {
              const cData = companyDoc.data();
              setCompanyName(cData.businessName || cData.name || "Your Company");
              setCompanyData(cData);
            }
            setDeveloperFeatureFlags(globalControlsDoc.exists() ? (globalControlsDoc.data()?.featureFlags || {}) : {});
          }
        } else {
          setUserRole("");
          setThemeMode(localStorage.getItem("supplyhawk-theme") || "light");
        }
      } catch (error) {
        console.error("❌ Error fetching company name:", error);
      }
    });

    return () => unsub();
  }, [navigate]);


  useEffect(() => {
    const normalizedTheme = themeMode === "dark" ? "dark" : "light";
    localStorage.setItem("supplyhawk-theme", normalizedTheme);
    document.documentElement.setAttribute("data-theme", normalizedTheme);
    document.body.setAttribute("data-theme", normalizedTheme);
  }, [themeMode]);

  useEffect(() => {
    const handleThemeChange = (event) => {
      const nextTheme = event?.detail?.theme === "dark" ? "dark" : "light";
      setThemeMode(nextTheme);
    };

    window.addEventListener("supplyhawk-theme-change", handleThemeChange);
    return () => window.removeEventListener("supplyhawk-theme-change", handleThemeChange);
  }, []);

  useEffect(() => {
    const handleOnline = () => setNetworkOnline(true);
    const handleOffline = () => setNetworkOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Find best route meta match
  const resolveMeta = (pathname) => {
    if (routeMeta[pathname]) return routeMeta[pathname];

    const keysBySpecificity = Object.keys(routeMeta).sort(
      (a, b) => b.length - a.length
    );
    const bestKey = keysBySpecificity.find(
      (k) => pathname === k || pathname.startsWith(k + "/")
    );

    return (bestKey && routeMeta[bestKey]) || { title: "SupplyHawk", subtitle: "" };
  };

  const meta = resolveMeta(pathKey);
  const userLabel = userFirstName || "Profile";
  const effectiveFeatureFlags = mergeFeatureFlags(companyData?.featureFlags || {}, developerFeatureFlags || {});
  const featureAccess = getFeatureAccessMap({
    featureFlags: effectiveFeatureFlags,
    userEmail,
    planType: companyData?.planType || userPlanType,
    subscriptionActive:
      typeof companyData?.subscriptionActive === "boolean"
        ? companyData.subscriptionActive
        : userSubscriptionActive,
    stripeSubscriptionId: companyData?.stripeSubscriptionId || "",
    seatLimits: companyData?.seatLimits || null,
  });
  const maintenanceBanner = getMaintenanceBannerConfig(effectiveFeatureFlags);
  const maintenanceBannerClasses = {
    sky: "border-sky-200 bg-sky-50 text-sky-900",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    rose: "border-rose-200 bg-rose-50 text-rose-900",
    violet: "border-violet-200 bg-violet-50 text-violet-900",
    slate: "border-slate-200 bg-slate-100 text-slate-900",
  };

  // Lightweight modal (no deps)
  const Modal = ({ open, title, children, onClose, maxWidth = "max-w-2xl" }) => {
    if (!open) return null;
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-3">
        <div className="absolute inset-0 bg-black/40" onClick={onClose} />
        <div
          className={`relative w-full ${maxWidth} bg-white rounded-2xl shadow-xl border border-slate-200`}
        >
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"
              aria-label="Close modal"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="p-4 max-h-[75vh] overflow-y-auto">{children}</div>
        </div>
      </div>
    );
  };

  const openLegal = (tab) => {
    setLegalTab(tab);
    setLegalOpen(true);
    setProfileMenuOpen(false);
  };

  useEffect(() => {
    const handleOpenLegal = (event) => {
      const nextTab = event?.detail?.tab === "privacy" ? "privacy" : "terms";
      openLegal(nextTab);
    };

    window.addEventListener("open-legal-modal", handleOpenLegal);
    return () => window.removeEventListener("open-legal-modal", handleOpenLegal);
  }, []);

  const openContact = () => {
    setContactOpen(true);
    setProfileMenuOpen(false);
  };

  return (
    <div className={`h-screen w-full max-w-full overflow-hidden overscroll-x-none ${themeMode === "dark" ? "app-theme-dark bg-[#0b1c30]" : "bg-slate-50"}`}>
      {/* ✅ Desktop sidebar only when NOT customer */}
      {!hideSidebar && (
        <SidebarNav
          companyName={companyName}
          onOpenContact={openContact}
          onOpenLegal={openLegal}
          featureAccess={featureAccess}
        />
      )}

      {/* Mobile menu / more drawer */}
      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        featureAccess={featureAccess}
      />

      {/* ✅ Main content area: only offset when sidebar exists */}
      <div
        className={[
          "h-screen w-full max-w-full flex flex-col min-w-0 overflow-x-hidden",
          hideSidebar ? "pl-0" : "md:pl-64",
        ].join(" ")}
      >
        {/* Header bar */}
        <header className="h-14 bg-gradient-to-b from-white via-slate-50 to-blue-50 border-b border-slate-200 flex items-center justify-between px-3 lg:px-5 shadow-sm flex-shrink-0">
          {/* Left */}
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {/* ✅ Only show hamburger for staff layout */}
            {!hideSidebar && (
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 transition-colors"
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5" />
              </button>
            )}

            <div className="hidden sm:flex w-9 h-9 rounded-xl bg-gradient-to-br from-slate-600 to-slate-800 items-center justify-center shadow-sm flex-shrink-0">
              <span className="text-white text-sm font-semibold">
                {companyName.charAt(0).toUpperCase()}
              </span>
            </div>

            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold text-slate-900 truncate">{meta.title}</h2>
              {meta.subtitle && (
                <p className="text-[11px] text-slate-500 truncate hidden md:block">{meta.subtitle}</p>
              )}
            </div>
          </div>

          {/* Right */}
          <div className="flex items-center gap-3">
            <div className="hidden lg:flex items-center gap-2">
              <img src={Logo} alt="SupplyHawk" className="h-6 w-auto object-contain" />
              <div className="hidden md:block leading-tight">
                <div className="text-[11px] font-semibold text-slate-900">TheSupplyHawk</div>
                <div className="text-[10px] text-slate-500">© TheSupplyHawk 2025</div>
              </div>
            </div>

            <NotificationBell currentUserId={currentUserId} companyId={companyId} />

            {/* Profile dropdown */}
            <div className="relative" ref={profileMenuRef}>
              <button
                type="button"
                onClick={() => setProfileMenuOpen((v) => !v)}
                className="flex items-center gap-2 px-2.5 py-1.5 rounded-full border border-slate-200 bg-white hover:bg-slate-50 transition-colors"
              >
                {userPhotoUrl ? (
                  <img
                    src={userPhotoUrl}
                    alt={userLabel}
                    className="h-6 w-6 rounded-full object-cover border border-slate-200"
                  />
                ) : (
                  <UserCircle2 className="w-5 h-5 text-slate-600" />
                )}
                <span className="hidden sm:inline text-xs font-medium text-slate-800">
                  {userLabel}
                </span>
                <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {profileMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden z-50">
                  <button
                    type="button"
                    onClick={() => {
                      setProfileMenuOpen(false);
                      navigate(buildAppPath((userRole || "").toLowerCase() === "customer" ? "/customer?view=company" : "/settings"));
                    }}
                    className="w-full text-left px-4 py-3 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    Profile & Company
                  </button>

                  <button
                    type="button"
                    onClick={openContact}
                    className="w-full text-left px-4 py-3 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    Contact TheSupplyHawk
                  </button>

                  <button
                    type="button"
                    onClick={() => openLegal("terms")}
                    className="w-full text-left px-4 py-3 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    Terms of Service
                  </button>

                  <button
                    type="button"
                    onClick={() => openLegal("privacy")}
                    className="w-full text-left px-4 py-3 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    Privacy Policy
                  </button>

                  <div className="h-px bg-slate-200" />

                  <button
                    type="button"
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className={`flex-1 px-3 md:px-5 pt-2 overflow-y-auto overflow-x-hidden min-w-0 ${showMobileShell ? "pb-28 md:pb-4" : "pb-4"}`}>
          {!networkOnline ? (
            <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
              Connection lost. SupplyHawk will reconnect when your internet comes back.
            </div>
          ) : null}
          {maintenanceBanner.enabled && maintenanceBanner.message ? (
            <div className={`mb-3 rounded-xl border px-4 py-2 text-sm ${maintenanceBannerClasses[maintenanceBanner.color] || maintenanceBannerClasses.sky}`}>
              {maintenanceBanner.message}
            </div>
          ) : null}
          <Outlet />
        </main>

        {/* Dev/Test footer */}
        {isDev && !hideSidebar && (
          <div className="bg-white px-3 md:px-5 py-2 flex items-center justify-between text-xs text-gray-500 border-t border-gray-200 flex-shrink-0">
            <span className="hidden sm:inline">Developer utilities (visible only to dev accounts)</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                // ✅ FIX: dev tools path should also be under /app if applicable
                onClick={() => navigate(buildAppPath("/dev-tools"))}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-gray-400 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Open Dev Tools
              </button>
              <button
                type="button"
                onClick={handleTestResend}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-blue-600 text-blue-600 hover:bg-blue-50 transition-colors"
              >
                Send Test Email
              </button>
            </div>
          </div>
        )}
      </div>

      {showMobileShell ? (
      <MobileBottomNav appBase={APP_BASE} featureAccess={featureAccess} />
      ) : null}

      {/* Contact modal */}
      <Modal
        open={contactOpen}
        title="Contact SupplyHawk"
        onClose={() => setContactOpen(false)}
        maxWidth="max-w-xl"
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-700">
            Need help, want to request a feature, or report an issue?
          </p>

          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            Emails send with your logged-in email as the reply-to, and are capped at 3 per day for safety.
            {contactRemainingToday != null ? ` Remaining today: ${contactRemainingToday}.` : ""}
          </div>

          {contactFeedback ? (
            <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700">
              {contactFeedback}
            </div>
          ) : null}

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
              Support Email
            </div>
            <div className="mt-2 flex items-center justify-between gap-3">
              <div className="text-sm font-medium text-slate-900">{SUPPORT_EMAIL}</div>
              <div className="text-[11px] text-slate-500">{userEmail || "Use your logged-in email"}</div>
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Include your company name + a screenshot if possible.
            </p>
            <div className="mt-3 space-y-2">
              <input
                type="text"
                value={contactForms.support.subject}
                onChange={(e) => updateContactField("support", "subject", e.target.value)}
                placeholder="Support subject"
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <textarea
                rows={3}
                value={contactForms.support.message}
                onChange={(e) => updateContactField("support", "message", e.target.value)}
                placeholder="Describe the issue or question..."
                className="w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => handleSendContact("support")}
                  disabled={contactSendingTarget === "support"}
                  className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-50 disabled:opacity-60"
                >
                  {contactSendingTarget === "support" ? "Sending…" : "Send Support Email"}
                </button>
              </div>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Sales & General Inquiries
              </div>
              <div className="mt-2 flex items-center justify-between gap-3">
                <div className="text-sm font-medium text-slate-900">{SALES_EMAIL}</div>
                <div className="text-[11px] text-slate-500">{userEmail || "Use your logged-in email"}</div>
              </div>
              <div className="mt-2 flex items-center justify-between gap-3">
                <div className="text-xs text-slate-500">{SALES_PHONE}</div>
                <a
                  href={`tel:${SALES_PHONE}`}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 bg-white hover:bg-slate-50"
                >
                  Call
                </a>
              </div>
              <div className="mt-3 space-y-2">
                <input
                  type="text"
                  value={contactForms.sales.subject}
                  onChange={(e) => updateContactField("sales", "subject", e.target.value)}
                  placeholder="Sales subject"
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <textarea
                  rows={3}
                  value={contactForms.sales.message}
                  onChange={(e) => updateContactField("sales", "message", e.target.value)}
                  placeholder="Tell us what you need..."
                  className="w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => handleSendContact("sales")}
                    disabled={contactSendingTarget === "sales"}
                    className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-50 disabled:opacity-60"
                  >
                    {contactSendingTarget === "sales" ? "Sending…" : "Send Sales Email"}
                  </button>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Technical Support
              </div>
              <div className="mt-2 flex items-center justify-between gap-3">
                <div className="text-sm font-medium text-slate-900">{TECH_SUPPORT_PHONE}</div>
                <a
                  href={`tel:${TECH_SUPPORT_PHONE}`}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 bg-white hover:bg-slate-50"
                >
                  Call
                </a>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setContactOpen(false)}
              className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>

      {/* Legal modal */}
      <Modal
        open={legalOpen}
        title={legalTab === "terms" ? "Terms of Service" : "Privacy Policy"}
        onClose={() => setLegalOpen(false)}
        maxWidth="max-w-3xl"
      >
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setLegalTab("terms")}
              className={[
                "px-3 py-2 rounded-lg text-sm font-medium border",
                legalTab === "terms"
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50",
              ].join(" ")}
            >
              Terms
            </button>
            <button
              type="button"
              onClick={() => setLegalTab("privacy")}
              className={[
                "px-3 py-2 rounded-lg text-sm font-medium border",
                legalTab === "privacy"
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50",
              ].join(" ")}
            >
              Privacy
            </button>
          </div>

          {legalTab === "terms" ? (
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p className="text-xs text-slate-500">
                General business terms for platform use. This is a practical baseline, but it should still be reviewed
                by your attorney for your jurisdiction and specific policies.
              </p>

              <h4 className="font-semibold text-slate-900">1. Scope of Service</h4>
              <p>
                SupplyHawk provides software tools for distributor operations, customer portals, accounts, inventory,
                sample-order workflows, notifications, and related business activities. Features may evolve over time
                and may rely on third-party providers such as hosting, messaging, authentication, and payment services.
              </p>

              <h4 className="font-semibold text-slate-900">2. Accounts & Security</h4>
              <p>
                You are responsible for accurate account information, safeguarding credentials, controlling access for
                your team, and promptly notifying us of suspected unauthorized use. We may suspend or restrict access if
                necessary to protect the platform, your data, or other users.
              </p>

              <h4 className="font-semibold text-slate-900">3. Acceptable Use</h4>
              <p>
                You may not misuse the service, exceed authorized permissions, scrape or reverse engineer the platform,
                upload unlawful or infringing content, send spam, or interfere with system integrity, security, or
                availability.
              </p>

              <h4 className="font-semibold text-slate-900">4. Orders, Samples, and Business Decisions</h4>
              <p>
                Sample requests, feedback, pricing signals, delivery targets, and related records are workflow tools
                only. Product suitability, allergen handling, food safety, fulfillment, and final commercial decisions
                remain the responsibility of the distributor and customer using the platform.
              </p>

              <h4 className="font-semibold text-slate-900">5. Billing, Trials, and Plan Changes</h4>
              <p>
                Trial access, paid subscriptions, user seats, and billing features are subject to the plan presented at
                the time of purchase or upgrade. We may change plans or features from time to time and may suspend or
                limit access for non-payment, abuse, fraud, or material violations of these terms, as permitted by law.
              </p>

              <h4 className="font-semibold text-slate-900">6. Availability, Data, and Disclaimers</h4>
              <p>
                The service is provided on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis to the fullest extent
                permitted by law. While we work to provide reliable service, we do not guarantee uninterrupted
                availability, error-free operation, or absolute accuracy of all customer-entered or third-party data.
              </p>

              <h4 className="font-semibold text-slate-900">7. Limitation of Liability</h4>
              <p>
                To the fullest extent permitted by law, SupplyHawk and its operators will not be liable for indirect,
                incidental, special, consequential, exemplary, or punitive damages, or for loss of profits, revenue,
                data, goodwill, or business opportunity arising out of or related to use of the platform.
              </p>

              <h4 className="font-semibold text-slate-900">8. Contact</h4>
              <p>
                Questions about these Terms? Contact{" "}
                <a className="text-blue-600 hover:underline" href={`mailto:${SUPPORT_EMAIL}`}>
                  {SUPPORT_EMAIL}
                </a>
                .
              </p>
            </div>
          ) : (
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p className="text-xs text-slate-500">
                Operational privacy summary for the platform. This should also be reviewed with legal counsel before you
                rely on it as a final public policy.
              </p>

              <h4 className="font-semibold text-slate-900">1. Information We Collect</h4>
              <p>
                We may collect user profile details, company records, customer and location information, sample-order
                activity, feedback submissions, billing-related details, support communications, device or browser
                signals, and usage data needed to operate and improve the service.
              </p>

              <h4 className="font-semibold text-slate-900">2. How We Use Data</h4>
              <p>
                We use information to provide the service, authenticate users, route orders and notifications, support
                customers, process billing, improve platform performance, detect abuse, maintain security, and comply
                with legal obligations.
              </p>

              <h4 className="font-semibold text-slate-900">3. Sharing</h4>
              <p>
                We do not sell personal information for cash. We may share information with trusted vendors and service
                providers that support infrastructure, communications, analytics, hosting, authentication, or payment
                processing when reasonably necessary to operate the platform.
              </p>

              <h4 className="font-semibold text-slate-900">4. Retention and Security</h4>
              <p>
                We retain information for as long as reasonably needed for operations, legal compliance, dispute
                resolution, contract enforcement, and legitimate business records. We use reasonable safeguards, but no
                system can guarantee absolute security.
              </p>

              <h4 className="font-semibold text-slate-900">5. Your Choices</h4>
              <p>
                Users can generally update certain profile, company, and location details inside the platform. You may
                also contact us about access, correction, or deletion requests, subject to legitimate business,
                contractual, and legal retention requirements.
              </p>

              <h4 className="font-semibold text-slate-900">6. Contact</h4>
              <p>
                Privacy questions? Contact{" "}
                <a className="text-blue-600 hover:underline" href={`mailto:${SUPPORT_EMAIL}`}>
                  {SUPPORT_EMAIL}
                </a>
                .
              </p>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={() => setLegalOpen(false)}
              className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AppLayout;
